// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import Switch from 'react-switch';

import Select2, { DataSelect2 } from 'wdna-select2';
import DatePicker, { DateSelectorConfig } from 'wdna-date-selector';

import { UserDO } from 'wdna-components/lib/types.new';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { ContextType, FilterContext } from '../../../../types';
import { typeSensorVariable, TypeSensorVariableDO, typeSensor, TypeSensorDO } from '../../../configuration/requests';
import { company, CompanyDO } from '../../../management/requests';
import { sensor, SensorDO } from '../../requests';

interface Props {
  rowId?: number;
}

interface State {
  companyArray: CompanyDO[];
  typeSensorArray: TypeSensorDO[];
  sensorArray: SensorDO[];

  dates: Date[];
  flatpickrMode: string;
  groupDateTime: string;

  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;

  checked: boolean;
  rounded: boolean;
  autoRefresh: boolean;

  sensorId?: number;
  companyId?: number;
  typeSensorId?: number;
}

export default class Filter extends React.PureComponent<Props, State> {
  private pid: string;
  private mounted: boolean;
  private userIsURW: boolean;

  private timer: NodeJS.Timeout | undefined;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.userIsURW = false;

    /* INITIAL FILTER STATE */
    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 1);
    const dEnd: Date = new Date();

    this.state = {
      companyArray: [],
      typeSensorArray: [],
      sensorArray: [],

      dates: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',

      checked: false,
      rounded: true,
      autoRefresh: false,
    };
  }

  /* React Lifecycle methods */
  public componentDidMount(): void {
    this.mounted = true;
    this.userIsURW = hasLevel('role', ['ADM', 'URW']);

    this.load();
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    if (_.isNil(this.props.rowId)) {
      this.loadFilter().catch(() => { /* pass */ });
    }
  };

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const twoDaysBefore: Date = moment().add(-2, 'days').toDate();
    return [twoDaysBefore, today];
  };

  public hideSidebar = (): void => {
    $('#right-panel').addClass('hidden-functions');
    $('#tab-main-content').removeClass('tab-left-content');
    $('#toggle-func-btn').addClass('transparent sidebar-hidden');
  };

  public loadFilter = async (): Promise<void> => {
    const companyFilter: Record<string, any> = {};
    if (!this.userIsURW) {
      const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);
      companyFilter.id = _.defaultTo(_.toNumber(user.access?.companyId), undefined);
      companyFilter.clientId = _.defaultTo(_.toNumber(user.access?.clientId), undefined);
    }

    this.loadingStart();
    try {
      const [typeSensorArray, companyArray] = await Promise.all([
        typeSensor<TypeSensorDO[]>('list', undefined, { active: true }, { params: { order: 'typeModule' } }) || [],
        company<CompanyDO[]>('list', undefined, companyFilter) || [],
      ]);
      this.setLocalState({
        typeSensorArray,
        companyArray,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadVariable = async (): Promise<void> => {
    const idSensor: number | undefined = this.state.sensorId || this.props.rowId;
    if (idSensor !== undefined) {
      const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor));
      const typeSensorId: number | undefined = sensorInfo?.typeSensorId;

      const typeVariableArray: TypeSensorVariableDO[] = await typeSensorVariable<TypeSensorVariableDO[]>('list', undefined, { typeSensorId }) || [];
      const nameVariables: string[] = typeVariableArray.map((item: TypeSensorVariableDO): string => {
        return `${item.variableName} ${item.variableUnit}`;
      });

      this.context?.updateParent({
        data3: nameVariables,
      });
    }
  };

  public loadSensors = async (): Promise<void> => {
    const { companyId, typeSensorId } = this.state;

    const request: Record<string, any> = {};
    if (companyId) request.companyId = companyId;
    if (typeSensorId) request.typeSensorId = typeSensorId;

    this.loadingStart();
    try {
      const sensorArray: SensorDO[] = await sensor<SensorDO[]>('list', undefined, request) || [];
      this.setLocalState({
        sensorId: undefined,
        sensorArray,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public submit = async (): Promise<void> => {
    if (!this.mounted) return;
    const { sensorId, dateIni, timeIni, dateEnd, timeEnd, rounded } = this.state;

    const idSensor: number | undefined = sensorId || this.props.rowId;
    if (!idSensor) { messageError('Error', 'Please select a device'); return; }

    const dIni: string | undefined = (dateIni) ? moment(`${dateIni}T${timeIni || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString() : undefined;
    const dEnd: string | undefined = (dateEnd) ? moment(`${dateEnd}T${timeEnd || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString() : undefined;

    // REQUEST: register_list
    const req: Record<string, any> = {
      round: rounded,
      from: dIni,
      to: dEnd,
    };

    if (this.isValidRequest(req)) {
      await this.loadVariable();
      this.loadingStart();
      try {
        const data: any[] = await sensor('register_list', String(idSensor), req) || [];
        this.context?.updateParent({ data });
      } catch (error) {
        messageError('Error', getErrorMessage(error));
      }
      this.loadingFinish();
      this.hideSidebar();
    }
  };

  public isValidRequest = (data: Record<string, any>): boolean => {
    const { from, to } = data;
    if (!from || !to) {
      messageError('Error', 'Please select valid dates for the interval');
      return false;
    }
    return true;
  };

  public updateMainFilter = (filter: any): void => {
    const newFilter: any = _.cloneDeep(filter);

    this.context?.updateParent({
      ...newFilter,
    });
  };

  public loadingStart = (): void => {
    this.context?.updateParent({ loading: true, });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({ loading: false, });
    window.store.dispatch(finishProcess(this.pid));
  };

  /* Handlers */
  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    this.handleChange(e);
    this.loadSensors().catch(() => { /* pass */ });
  };

  public handleChangeDatePicker = (config: DateSelectorConfig): void => {
    const { flatpickrMode } = this.state;

    const hours: string[] = config.getHours() as string[];
    const dates: Date[] = config.getDates() as Date[];
    const newDataIni: moment.Moment = moment(dates[0]);
    const newDataEnd: moment.Moment = (flatpickrMode === 'range') ? moment(dates[1]) : moment(newDataIni).add(1, 'days');

    this.setLocalState({
      dates,
      timeIni: hours[0],
      timeEnd: hours[1],
      dateIni: newDataIni.format('YYYY-MM-DD'),
      dateEnd: newDataEnd.format('YYYY-MM-DD'),
      flatpickrMode: config.getMode(),
    });
  };

  public handleSubmit = (): void => {
    this.submit().catch(() => { /* pass */ });
  };

  public refresh(create: boolean): void {
    if (create) {
      this.timer = setInterval(() => { this.handleSubmit(); }, 60000);
    } else {
      clearInterval(this.timer);
    }
  }

  public handleAutoRefresh = (autoRefresh: boolean): void => {
    this.setLocalState({
      autoRefresh,
    }, () => { this.refresh(autoRefresh); });
  };

  public handleSwitch = (checked: boolean, _event: React.SyntheticEvent<MouseEvent | KeyboardEvent> | MouseEvent, id: string): void => {
    this.setLocalState({ [id]: checked });
  };

  public render(): JSX.Element {
    const { companyArray, typeSensorArray, sensorArray } = this.state;
    const { sensorId, dates, flatpickrMode, groupDateTime, companyId, typeSensorId, autoRefresh, rounded } = this.state;

    const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);
    const showMainFilter: boolean = (this.props.rowId == null);

    return (
      <div className="main-filters">
        <div className="auto-refresh-button">
          <br />
          <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
            <Switch
              id="auto-refresh-button"
              className="react-switch"
              aria-labelledby="neat-label"
              height={30}
              onColor="#78a329"
              offColor="#5c6875"
              offHandleColor="#444"
              onHandleColor="#b7f93d"
              checked={autoRefresh}
              onChange={this.handleAutoRefresh}
            />
            <span style={{ marginLeft: '10px' }}>Auto Refresh</span>
          </label>
        </div>
        <div className="auto-refresh-button">
          <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
            <Switch
              id="rounded"
              className="react-switch"
              aria-labelledby="neat-label"
              height={30}
              onColor="#78a329"
              offColor="#5c6875"
              offHandleColor="#444"
              onHandleColor="#b7f93d"
              checked={rounded}
              onChange={this.handleSwitch}
            />
            <span style={{ marginLeft: '10px' }}>Round values (2 dec)</span>
          </label>
        </div>
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="companyId"
              value={companyId}
              onChange={this.handleChangeFilter}
              data-ctype="number"
              data={(companyArray || [])
                .filter((item: CompanyDO) => {
                  return this.userIsURW || String(item.id) === user?.access?.companyId;
                })
                .map((item: CompanyDO): DataSelect2 => {
                  return {
                    id: item.id,
                    text: item.name,
                    title: item.name,
                  };
                })}
              options={{ placeholder: 'Select Company' }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="typeSensorId"
              value={typeSensorId}
              onChange={this.handleChangeFilter}
              data-ctype="number"
              data={(typeSensorArray || []).map((item: TypeSensorDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.name}`,
                  title: `${item.name} [${item.code}]`,
                };
              })}
              options={{ placeholder: 'Select Sensor Type' }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="sensorId"
              value={sensorId}
              onChange={this.handleChange}
              data-ctype="number"
              data={(sensorArray || []).map((item: SensorDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.description}`,
                  title: `${item.code} - ${item.name}`,
                };
              })}
              options={{ placeholder: 'Select Sensor To Show' }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <DatePicker
          icon
          hours={[]}
          dates={dates}
          showChangeModes
          mode={flatpickrMode}
          granularity={groupDateTime}
          iconClass="wdna-ico-icon-calendar"
          handleChanges={this.handleChangeDatePicker}
        />
        <div style={{ marginTop: '1px' }}>
          <button type="button" className="filter-btn btn btn-default" onClick={(): void => { this.submit().catch(() => { /* pass */ }); }}>
            <i className="wdna-ico-icon-playsearch" />
            <span>GO</span>
          </button>
        </div>
      </div>
    );
  }
}
